import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, RotateCcw, Volume2, VolumeX, Plus, Minus, Target } from 'lucide-react';

const dhikrPresets = [
  { name: "Subhanallah", arabic: "سُبْحَانَ اللهِ", meaning: "I lartësuar qoftë Allahu", defaultCount: 33 },
  { name: "Alhamdulillah", arabic: "الْحَمْدُ لِلَّهِ", meaning: "Falënderimi i takon Allahut", defaultCount: 33 },
  { name: "Allahu Ekber", arabic: "اللهُ أَكْبَرُ", meaning: "Allahu është më i Madhi", defaultCount: 33 },
  { name: "La ilahe il-lAllah", arabic: "لَا إِلَٰهَ إِلَّا اللهُ", meaning: "Nuk ka zot tjetër përveç Allahut", defaultCount: 100 },
  { name: "Astaghfirullah", arabic: "أَسْتَغْفِرُ اللهَ", meaning: "Kërkoj falje nga Allahu", defaultCount: 100 },
  { name: "Salavate", arabic: "اللَّهُمَّ صَلِّ عَلَى مُحَمَّد", meaning: "O Allah, dërgo salavate mbi Muhamedin", defaultCount: 100 },
];

const TasbihCounter = ({ isOpen, onClose }) => {
  const [selectedDhikr, setSelectedDhikr] = useState(dhikrPresets[0]);
  const [count, setCount] = useState(0);
  const [targetCount, setTargetCount] = useState(33);
  const [totalCount, setTotalCount] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [completed, setCompleted] = useState(false);

  // Load saved data from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('tasbihData');
    if (saved) {
      const data = JSON.parse(saved);
      setTotalCount(data.totalCount || 0);
    }
  }, []);

  // Save to localStorage
  useEffect(() => {
    localStorage.setItem('tasbihData', JSON.stringify({ totalCount }));
  }, [totalCount]);

  const playClickSound = useCallback(() => {
    if (soundEnabled) {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.1);
    }
  }, [soundEnabled]);

  const playCompleteSound = useCallback(() => {
    if (soundEnabled) {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 523.25; // C5
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    }
  }, [soundEnabled]);

  const handleCount = () => {
    if (count < targetCount) {
      const newCount = count + 1;
      setCount(newCount);
      setTotalCount(prev => prev + 1);
      playClickSound();
      
      if (newCount === targetCount) {
        setCompleted(true);
        playCompleteSound();
        setTimeout(() => setCompleted(false), 2000);
      }
    }
  };

  const handleReset = () => {
    setCount(0);
    setCompleted(false);
  };

  const handleDhikrSelect = (dhikr) => {
    setSelectedDhikr(dhikr);
    setTargetCount(dhikr.defaultCount);
    setCount(0);
    setCompleted(false);
  };

  const adjustTarget = (delta) => {
    setTargetCount(prev => Math.max(1, prev + delta));
  };

  if (!isOpen) return null;

  const progress = (count / targetCount) * 100;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="tasbih-overlay"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="bg-white rounded-3xl max-w-lg w-full overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          data-testid="tasbih-modal"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-primary to-primary-600 p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-heading font-bold">Tesbih / Dhikr</h2>
                <p className="text-white/70 text-sm">Numërues dixhital</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSoundEnabled(!soundEnabled)}
                  className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30"
                  data-testid="tasbih-sound-toggle"
                >
                  {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
                </button>
                <button
                  onClick={onClose}
                  className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30"
                  data-testid="tasbih-close-btn"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="mt-4 text-sm">
              <span className="text-white/70">Gjithsej dhikr: </span>
              <span className="font-bold">{totalCount.toLocaleString()}</span>
            </div>
          </div>

          {/* Dhikr Selection */}
          <div className="p-4 bg-gray-50 border-b overflow-x-auto">
            <div className="flex gap-2">
              {dhikrPresets.map((dhikr, index) => (
                <button
                  key={index}
                  onClick={() => handleDhikrSelect(dhikr)}
                  className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedDhikr.name === dhikr.name
                      ? 'bg-primary text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
                  data-testid={`dhikr-${dhikr.name.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {dhikr.name}
                </button>
              ))}
            </div>
          </div>

          {/* Counter Display */}
          <div className="p-8">
            {/* Arabic Text */}
            <div className="text-center mb-6">
              <p className="text-4xl font-arabic text-primary mb-2" style={{ fontFamily: 'Amiri, serif' }}>
                {selectedDhikr.arabic}
              </p>
              <p className="text-lg font-semibold text-gray-700">{selectedDhikr.name}</p>
              <p className="text-sm text-gray-500">{selectedDhikr.meaning}</p>
            </div>

            {/* Progress Ring */}
            <div className="relative w-48 h-48 mx-auto mb-6">
              <svg className="w-full h-full transform -rotate-90">
                <circle
                  cx="96"
                  cy="96"
                  r="88"
                  stroke="#e5e7eb"
                  strokeWidth="12"
                  fill="none"
                />
                <motion.circle
                  cx="96"
                  cy="96"
                  r="88"
                  stroke={completed ? "#D97706" : "#065F46"}
                  strokeWidth="12"
                  fill="none"
                  strokeLinecap="round"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: progress / 100 }}
                  transition={{ duration: 0.3 }}
                  style={{
                    strokeDasharray: "553",
                    strokeDashoffset: 553 - (553 * progress) / 100,
                  }}
                />
              </svg>
              
              {/* Counter Button */}
              <motion.button
                onClick={handleCount}
                whileTap={{ scale: 0.95 }}
                className={`absolute inset-4 rounded-full flex flex-col items-center justify-center transition-colors ${
                  completed 
                    ? 'bg-accent text-white' 
                    : 'bg-primary text-white hover:bg-primary-600'
                }`}
                data-testid="tasbih-count-btn"
              >
                <span className="text-5xl font-heading font-bold">{count}</span>
                <span className="text-sm opacity-80">/ {targetCount}</span>
              </motion.button>
            </div>

            {/* Target Adjustment */}
            <div className="flex items-center justify-center gap-4 mb-6">
              <button
                onClick={() => adjustTarget(-10)}
                className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200"
                data-testid="tasbih-decrease-btn"
              >
                <Minus className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2 text-gray-600">
                <Target className="w-5 h-5" />
                <span className="font-semibold">Qëllimi: {targetCount}</span>
              </div>
              <button
                onClick={() => adjustTarget(10)}
                className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200"
                data-testid="tasbih-increase-btn"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>

            {/* Reset Button */}
            <button
              onClick={handleReset}
              className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
              data-testid="tasbih-reset-btn"
            >
              <RotateCcw className="w-5 h-5" />
              Rifillo
            </button>

            {completed && (
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center mt-4 text-accent font-semibold"
              >
                🎉 Masha'Allah! Qëllimi u arrit!
              </motion.p>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TasbihCounter;
